/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import AsyncStatusIndicator from './AsyncStatusIndicator'
import Button from './Button'

import PeripheralBridge from '../utils/peripheralBridge'
import { isOfflineApproval } from '../utils/offlineReplayStatuses'
import { invoiceNumber } from '../utils/orderNumbers'

import styles from './CardReader.module.scss'

const getAuthType = (authType) => {
  switch (authType) {
    case 'sale': return 'executeSaleRequest'
    case 'auth': return 'executeAuthRequest'
    default: return 'executeAuthRequest'
  }
}

const CardReader = ({
  authorizationType,
  className,
  exemptions,
  onCancel,
  onDone,
  onFailed,
  onRemotePayment,
  onRetry,
  onSuccess,
  paymentPreference,
  paymentOptions,
  peripheralStatus,
  total,
  uuid,
  isNewTab,
  orderNumber,
  ...props
}) => {
  const classNames = cn(styles.cardReader, className)
  const [status, setStatus] = useState('ready')
  const method = getAuthType(authorizationType)

  const STATUS = {
    'ready': {
      cardReaderStatusText: 'Card Reader is Ready',
      subtitle: 'Swipe, dip, or tap',
      buttons:  [],
    },
    'loading': {
      cardReaderStatusText: 'Processing...',
      subtitle: 'Please wait',
      buttons: ['cancelButton'],
    },
    'offline': {
      cardReaderStatusText: 'Offline',
      subtitle: 'Transaction attempt was made while device was offline.',
      buttons: ['tryAgainButton'],
    },
    'failed': {
      cardReaderStatusText: 'Declined',
      subtitle: 'This card was declined.',
      buttons: ['tryAgainButton'],
    },
    'succeeded': {
      cardReaderStatusText: 'Approved!',
      subtitle: '',
      buttons: isNewTab ? ['doneButton'] : [],
    },
    'removeCard': {
      cardReaderStatusText: 'Processing Complete',
      subtitle: 'Please remove your card.',
      buttons: [],
    },
    'insertCard': {
      cardReaderStatusText: 'Insert Card',
      subtitle: 'Please insert your card.',
      buttons: [],
    },
    'reInsertCard': {
      cardReaderStatusText: 'Insert Card',
      subtitle: 'Please try insert again.',
      buttons: [],
    },
    'swipeCard': {
      cardReaderStatusText: 'Swipe Card',
      subtitle: 'Please try card swipe.',
      buttons: [],
    },
    'swipeAgain': {
      cardReaderStatusText: 'Swipe Card',
      subtitle: 'Please try card swipe again.',
      buttons: [],
    },
    'noCardReader': {
      cardReaderStatusText: 'Card Reader Not Connected',
      subtitle: 'Please check connection.',
      buttons: [],
    },
    'attached': {
      cardReaderStatusText: 'Card Reader Not Ready',
      subtitle: 'Establishing Connection...',
      buttons: [],
    },
  }

  const mobyCardReaderEvent = {
    CARD_INSERT_DETECTED: 'loading',
    TAP_DETECTED: 'loading',
    CARD_SHOULD_BE_REMOVED: 'removeCard',
    CHIP_CARD_MUST_BE_INSERTED: 'insertCard',
    CHIP_CARD_REINSERT_REQUIRED: 'reInsertCard',
    SWIPE_MAG_CARD: 'swipeCard',
    SWIPE_ERROR_TRY_AGAIN: 'swipeAgain',
    WAITING_FOR_CARD: 'ready',
  }

  const CardReaderViewModel = ({ status, buttonsMap }) => {
    const { cardReaderStatusText, buttons: buttonStrings, subtitle } = STATUS[status]
    const buttons = buttonStrings.map((buttonString) => buttonsMap[buttonString])

    return {
      buttons,
      cardReaderStatusText,
      subtitle,
    }
  }

  const handleOnRetry = () => {
    setStatus('ready')
    onRetry()

    const bridgeInstance = PeripheralBridge.getBridge()

    if (bridgeInstance) {
      bridgeInstance.call(method, { uuid, amount: total, orderNumber, generateToken: true })
    }
  }

  const viewModel = CardReaderViewModel({
    status,
    buttonsMap: {
      cancelButton: <Button key="cancel" onClick={onCancel} className={styles.actionButton}>Cancel</Button>,
      tryAgainButton: <Button key="retry" onClick={handleOnRetry} className={styles.actionButton}>Try Again</Button>,
      doneButton: <Button key="done" onClick={onDone} className={styles.actionButton}>Done</Button>,
    },
  })

  const { cardReaderStatusText, buttons, subtitle } = viewModel

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance || peripheralStatus !== 'ready') {
      if (peripheralStatus === 'attached') {
        setStatus('attached')
      } else {
        setStatus('noCardReader')
      }
      return
    }

    bridgeInstance.registerHandler('progressMessageChanged', function(status) {
      if (mobyCardReaderEvent[status]) {
        setStatus(mobyCardReaderEvent[status])
      }
    })

    bridgeInstance.registerHandler('handleResult', function(data, callback) {
      if (data.status === 'ACCEPT' && (data.transType === 'auth' || data.transType === 'sale')) {
        if (isOfflineApproval(data)) {
          setStatus('offline')
          return
        }
        const dataWithUuid = { ...data, uuid }

        setStatus('succeeded')
        onSuccess(dataWithUuid)

      } else {
        setStatus('failed')
        onFailed()
      }

      var response = { 'message': data }
      callback(response)
    })

    bridgeInstance.call(method, { uuid, amount: total, orderNumber, generateToken: true })
  }, [uuid, total, peripheralStatus])

  const cancelCardRead = () => {
    const bridgeInstance = PeripheralBridge.getBridge()
    if (!bridgeInstance) return
    bridgeInstance.call('cancelTransactionAttempt')
  }

  useEffect(() => () => {
    if (status !== 'succeeded') {
      cancelCardRead()
    }
  }, [])

  return (
    <div {...props} className={classNames}>
      <div className={styles.border}></div>
      <div className={styles.bodyContainer}>
        <div className={styles.cardReaderStatus}>
          <p className={styles.cardReaderStatusText}>{cardReaderStatusText}</p>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>
        <AsyncStatusIndicator className={styles.cardReaderCircle} status={status} />
      </div>
      <div className={styles.buttonContainer}>
        {buttons}
      </div>
    </div>
  )
}

CardReader.defaultProps = {
  uuid: '',
  total: '',
  authorizationType: 'auth',
  exemptions: {},
  paymentPreference: {},
  onCancel: () => {},
  onRetry: () => {},
  onSuccess: () => {},
  onFailed: () => {},
  onDone: () => {},
  onRemotePayment: () => {},
}

CardReader.propTypes = {
  uuid: PropTypes.string,
  total: PropTypes.string,
  authorizationType: PropTypes.oneOf(['sale', 'auth']),
  exemptions: PropTypes.object,
  paymentPreference: PropTypes.shape({
    paymentDisplay: PropTypes.string,
  }),
  onCancel: PropTypes.func,
  onRetry: PropTypes.func,
  onSuccess: PropTypes.func,
  onFailed: PropTypes.func,
  onDone: PropTypes.func,
  onRemotePayment: PropTypes.func,
}

export default CardReader
